import { useState, type FC, type ReactNode } from "react";
import { ActivityIndicator, Pressable, ScrollView } from "react-native";
import { StyleSheet } from "react-native-unistyles";
import { Banner, Button, Input, RadioButton, Text, View } from "@visioni/a3s-ui/components";
import "@visioni/a3s-ui/theme";
import {
  ANNOUNCEMENT_TEMPLATE_NAME_PREFIX,
  MAX_UPLOAD_BYTES,
  type AnnouncementTemplate,
  type RecipientList,
  type SendAnnouncementResponse,
} from "../../client-messages/announcements";
import { announcementFormState } from "./announcement-form";
import { AnnouncementSendDialog, type AnnouncementSendFailure } from "./AnnouncementSendDialog";
import { AnnouncementsApiError, type AnnouncementsApiConfig } from "./api";
import { useAnnouncementTemplates, useSendAnnouncement, useTestSendAnnouncement, useUploadList } from "./hooks";

export interface AnnouncementsPageProps {
  /** Outside production the email service drops every address not on its whitelist; the page says so. */
  readonly isProduction: boolean;
  /** The root API, as the `@visioni/a3s-dir/app` hooks reach it. */
  readonly api: AnnouncementsApiConfig;
}

type SendDialog = {
  idempotencyKey: string;
  listId: string;
  listDescription: string;
  recipientCount: number;
  templateId: string;
  templateName: string;
  subject: string;
};

const errorMessage = (error: unknown): string => (error instanceof Error ? error.message : "Something went wrong");

const failureOf = (error: unknown): AnnouncementSendFailure => ({
  reason: error instanceof AnnouncementsApiError ? error.reason : undefined,
  message: errorMessage(error),
  previous: error instanceof AnnouncementsApiError ? error.body?.previous : undefined,
});

const formatDate = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

const describeList = (list: RecipientList) =>
  `Uploaded by ${list.createdByEmail || "unknown"}, ${formatDate(list.createdAt)}`;

type PickedFile = { kind: "picked"; text: string } | { kind: "none" } | { kind: "tooLarge" };

/**
 * Web only: asks the browser for a CSV file and reads it. A file far over the
 * upload limit is refused before it is read at all (its UTF-8 text, checked
 * after reading, is what the limit is on).
 */
function pickCsvFile(): Promise<PickedFile> {
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".csv,.txt,text/csv,text/plain";
    input.onchange = () => {
      const file = input.files?.[0];
      if (file === undefined) {
        resolve({ kind: "none" });
        return;
      }
      if (file.size > 4 * MAX_UPLOAD_BYTES) {
        resolve({ kind: "tooLarge" });
        return;
      }
      void file.text().then((text) => {
        resolve({ kind: "picked", text });
      });
    };
    input.click();
  });
}

/**
 * Support-only page for emailing a feature announcement to an approved list:
 * upload a CSV and review its summary, pick a SendGrid `[Announcement]`
 * template and subject, send yourself a test, then send to exactly that list.
 */
export const AnnouncementsPage: FC<AnnouncementsPageProps> = ({ isProduction, api }) => {
  const templates = useAnnouncementTemplates(api);
  const upload = useUploadList(api);
  const testSend = useTestSendAnnouncement(api);
  const send = useSendAnnouncement(api);

  const [list, setList] = useState<RecipientList | undefined>(undefined);
  const [uploadError, setUploadError] = useState<string | undefined>(undefined);

  const [templateId, setTemplateId] = useState<string | undefined>(undefined);
  const [subject, setSubject] = useState("");
  const [notice, setNotice] = useState<{ variant: "info" | "warning"; message: string } | undefined>(undefined);
  /**
   * What the open send dialog confirms, captured when it opened: one
   * idempotency key (so a retry from it can never send twice), and the exact
   * list, count, template and subject the user is typing the count for.
   */
  const [dialog, setDialog] = useState<SendDialog | undefined>(undefined);
  const [sent, setSent] = useState<SendAnnouncementResponse | undefined>(undefined);

  const template = templates.data?.templates.find((t) => t.id === templateId);
  const form = announcementFormState({ template, subject, listId: list?.id });

  if (templates.error instanceof AnnouncementsApiError && templates.error.notAvailable) {
    return (
      <Page isProduction={isProduction}>
        <Banner
          variant="info"
          title="Announcements are not available in this environment"
          message="They run in the root account, which this environment (for example a merge-request environment) does not have."
        />
      </Page>
    );
  }

  const uploadCsv = async () => {
    setUploadError(undefined);
    const file = await pickCsvFile();
    if (file.kind === "none") return;
    // The API limits the body it receives: the file's text as UTF-8, which is what is sent.
    if (file.kind === "tooLarge" || new TextEncoder().encode(file.text).length > MAX_UPLOAD_BYTES) {
      setUploadError("The file is larger than 5 MB.");
      return;
    }
    upload.mutate(file.text, {
      onSuccess: setList,
      // The message names any invalid rows by number.
      onError: (error) => setUploadError(errorMessage(error)),
    });
  };

  const runTestSend = () => {
    if (template === undefined) return;
    setNotice(undefined);
    testSend.mutate(
      { templateId: template.id, subject: subject.trim() },
      {
        onSuccess: ({ sentTo }) => setNotice({ variant: "info", message: `Test queued to ${sentTo}.` }),
        onError: (error) => setNotice({ variant: "warning", message: errorMessage(error) }),
      },
    );
  };

  const openSendDialog = () => {
    if (list === undefined || template === undefined) return;
    send.reset();
    setSent(undefined);
    setDialog({
      idempotencyKey: globalThis.crypto.randomUUID(),
      listId: list.id,
      listDescription: describeList(list),
      recipientCount: list.recipientCount,
      templateId: template.id,
      templateName: template.name,
      subject: subject.trim(),
    });
  };

  // The dialog stands for one list and one email: if any of them changes underneath it, it closes.
  const dialogStillCurrent =
    dialog !== undefined &&
    dialog.listId === list?.id &&
    dialog.templateId === template?.id &&
    dialog.subject === subject.trim();

  const confirmSend = ({ allowResend }: { allowResend: boolean }) => {
    if (dialog === undefined || !dialogStillCurrent) return;
    const replacesSendId = send.error instanceof AnnouncementsApiError ? send.error.body?.previous?.sendId : undefined;
    send.mutate(
      {
        listId: dialog.listId,
        templateId: dialog.templateId,
        subject: dialog.subject,
        idempotencyKey: dialog.idempotencyKey,
        // A resend names the earlier send the user was shown, read from the current refusal.
        ...(allowResend && replacesSendId !== undefined && { allowResend: true, replacesSendId }),
      },
      {
        onSuccess: (result) => {
          setSent(result);
          setDialog(undefined);
        },
      },
    );
  };

  return (
    <Page isProduction={isProduction}>
      <Section
        title="1. Recipients"
        description="A CSV or text file with one email address per line (an optional 'email' header row is fine)."
      >
        <View gap="md" style={styles.card}>
          <View alignItems="flex-start">
            <Button
              label={list === undefined ? "Choose file…" : "Choose another file…"}
              size="sm"
              variant="secondary"
              onPress={() => void uploadCsv()}
              loading={upload.isPending}
              loadingText="Uploading…"
            />
          </View>
          {uploadError !== undefined && <Banner variant="warning" message={uploadError} />}
          {list !== undefined && <ListSummary list={list} />}
        </View>
      </Section>

      <Section title="2. Template and subject">
        {templates.isLoading ?
          <ActivityIndicator />
        : templates.error ?
          <Banner variant="warning" message={`Could not load templates: ${errorMessage(templates.error)}`} />
        : templates.data?.templates.length === 0 ?
          <Text textStyle="sm/regular" color="textMuted">
            {`No SendGrid dynamic templates are named “${ANNOUNCEMENT_TEMPLATE_NAME_PREFIX} …” yet.`}
          </Text>
        : templates.data?.templates.map((t) => (
            <TemplateOption key={t.id} template={t} selected={t.id === templateId} onSelect={setTemplateId} />
          ))
        }
        <Input
          label="Subject"
          floatLabel="always"
          value={subject}
          onChangeText={setSubject}
          placeholder="What's new in A3S"
          size="small"
        />
      </Section>

      <Section title="3. Test and send">
        <View flexDirection="row" gap="md" flexWrap="wrap">
          <Button
            label="Send test to me"
            size="sm"
            variant="secondary"
            onPress={runTestSend}
            disabled={!form.canTestSend}
            loading={testSend.isPending}
            loadingText="Sending test…"
          />
          <Button
            label={list === undefined ? "Send…" : `Send to ${String(list.recipientCount)}…`}
            size="sm"
            onPress={openSendDialog}
            disabled={!form.canSend}
          />
        </View>
        {notice !== undefined && <Banner variant={notice.variant} message={notice.message} />}
        {sent !== undefined && (
          <Banner
            variant="info"
            title={`Queued ${String(sent.queued)} emails`}
            message={
              `Send ID ${sent.sendId}. ` +
              (sent.removedAsUnsubscribed > 0 ?
                `${String(sent.removedAsUnsubscribed)} on the list unsubscribed since it was made and were left out. `
              : "") +
              `SendGrid Activity lists them under the category announcement:${sent.sendId}.`
            }
          />
        )}
      </Section>

      {dialog !== undefined && dialogStillCurrent && (
        <AnnouncementSendDialog
          key={dialog.idempotencyKey}
          templateName={dialog.templateName}
          subject={dialog.subject}
          listDescription={dialog.listDescription}
          recipientCount={dialog.recipientCount}
          sending={send.isPending}
          // Read from the mutation's current state, not from a callback's closure.
          failure={send.error ? failureOf(send.error) : undefined}
          onSend={confirmSend}
          onClose={() => setDialog(undefined)}
        />
      )}
    </Page>
  );
};

const Page: FC<{ isProduction: boolean; children: ReactNode }> = ({ isProduction, children }) => (
  <ScrollView style={styles.scroll} contentContainerStyle={styles.content}>
    <View gap="4xl">
      <View gap="xs">
        <Text textStyle="lg/semibold" color="text">
          Announcements
        </Text>
        <Text textStyle="sm/regular" color="textMuted">
          Email a feature announcement to an approved list. Design the email in SendGrid as a dynamic template named “
          {ANNOUNCEMENT_TEMPLATE_NAME_PREFIX} …” with an unsubscribe link.
        </Text>
      </View>
      {!isProduction && (
        <Banner
          variant="warning"
          message="Outside production only whitelisted addresses receive email, but an uploaded list may still hold real customers' addresses."
        />
      )}
      {children}
    </View>
  </ScrollView>
);

const Section: FC<{ title: string; description?: string; children: ReactNode }> = ({
  title,
  description,
  children,
}) => (
  <View gap="md">
    <View gap="xxs">
      <Text textStyle="sm/semibold" color="text">
        {title}
      </Text>
      {description !== undefined && (
        <Text textStyle="xs/regular" color="textMuted">
          {description}
        </Text>
      )}
    </View>
    {children}
  </View>
);

const ListSummary: FC<{ list: RecipientList }> = ({ list }) => (
  <View gap="sm">
    <Text textStyle="md/semibold" color="text">
      {`${String(list.recipientCount)} recipients`}
    </Text>
    <Text textStyle="xs/regular" color="textMuted">
      {`${describeList(list)}. Expires ${formatDate(list.expiresAt)}; upload it again to send after that.`}
    </Text>
    <View gap="xxs">
      <Text textStyle="xs/regular" color="text">
        {`Duplicate addresses removed: ${String(list.duplicates)}`}
      </Text>
      <Text textStyle="xs/regular" color="text">
        {`Unsubscribed addresses removed: ${String(list.unsubscribed)}`}
      </Text>
    </View>
    <View gap="xxs">
      <Text textStyle="xs/medium" color="text">
        {list.recipientCount > list.sample.length ? `First ${String(list.sample.length)} addresses` : "Addresses"}
      </Text>
      {list.sample.map((email) => (
        <Text key={email} textStyle="xs/regular" color="textMuted">
          {email}
        </Text>
      ))}
    </View>
  </View>
);

const TemplateOption: FC<{
  template: AnnouncementTemplate;
  selected: boolean;
  onSelect: (id: string) => void;
}> = ({ template, selected, onSelect }) => (
  <Pressable
    onPress={() => template.hasUnsubscribeTag && onSelect(template.id)}
    disabled={!template.hasUnsubscribeTag}
    style={styles.option}
  >
    <RadioButton selected={selected} onSelect={() => onSelect(template.id)} disabled={!template.hasUnsubscribeTag} />
    <View gap="xxs" flex={1}>
      <Text textStyle="sm/medium" color={template.hasUnsubscribeTag ? "text" : "textMuted"}>
        {template.name}
      </Text>
      <Text textStyle="xs/regular" color="textMuted">
        {template.hasUnsubscribeTag ?
          `Last edited ${template.activeVersionUpdatedAt ?? "unknown"}`
        : "Cannot be sent: the active version has no {{{unsubscribe}}} link."}
      </Text>
    </View>
  </Pressable>
);

const styles = StyleSheet.create((theme) => ({
  scroll: { flex: 1 },
  content: {
    padding: theme.spacing.xl,
    maxWidth: 760,
    width: "100%",
  },
  card: {
    padding: theme.spacing["3xl"],
    borderRadius: theme.spacing.xl,
    borderWidth: 1,
    borderColor: theme.colors.borderCard,
    backgroundColor: theme.colors.surfaceCard,
  },
  option: {
    flexDirection: "row",
    alignItems: "center",
    gap: theme.spacing.md,
    paddingVertical: theme.spacing.xs,
  },
}));
